import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import { toast } from 'react-toastify';

import { createExitEntryRequest } from '../../services/exitEntryApi';
import { PageHeader, PageLayout, SectionCard } from '../../components/ui';

const visaOptions = ['choose', 'One Visit', 'Multiple Visits'];

const initialExitEntryForm = {
  validity_from_date: '',
  validity_to_date: '',
  visa_type: 'choose',
  country: '',
  phone_number: '',
  location_in_country: '',
  period_in_days: '',
  reason: '',
  document: null,
};

const exitEntryWorkflowSteps = [
  { actor: 'Employee', action: 'Submit request and travel window.' },
  { actor: 'HR Admin', action: 'Review details and approve or reject.' },
  { actor: 'Employee', action: 'Track status from request history.' },
];

function getInclusiveDaysBetween(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  return Math.floor((endDay - startDay) / 86400000) + 1;
}

function FieldGroup({ icon, title, description, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        {icon}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

function FieldGrid({ children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 1.2,
      }}
    >
      {children}
    </Box>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ color: 'text.secondary', display: 'grid', placeItems: 'center', mt: 0.1 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 850, overflowWrap: 'anywhere' }}>
          {value || '-'}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function ExitEntryFormPage() {
  const navigate = useNavigate();
  const theme = useTheme();

  const [formData, setFormData] = useState(initialExitEntryForm);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const { validity_from_date, validity_to_date, visa_type, period_in_days } = formData;
  const calculatedDays = getInclusiveDaysBetween(validity_from_date, validity_to_date);
  const selectedVisaType = visa_type === 'choose' ? '-' : visa_type;

  const requiredStatus = useMemo(
    () => [
      { label: 'From date', done: Boolean(validity_from_date) },
      { label: 'To date', done: Boolean(validity_to_date) },
      { label: 'Visa type', done: visa_type !== 'choose' },
    ],
    [validity_from_date, validity_to_date, visa_type]
  );
  const completionPct = Math.round((requiredStatus.filter((item) => item.done).length / requiredStatus.length) * 100);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.hr?.radius?.md || 2,
      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.74 : 0.92),
    },
  };

  const handleChange = (event) => {
    const { name, value, files } = event.target;
    if (name === 'document') {
      setFormData((prev) => ({ ...prev, document: files?.[0] || null }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setValidationErrors([]);

    try {
      await createExitEntryRequest(formData);
      toast.success('Exit/Entry request created!');
      setFormData(initialExitEntryForm);
    } catch (err) {
      console.error('Error creating request:', err);
      if (err.response?.status === 422 && err.response.data?.errors) {
        const serverErrors = err.response.data.errors;
        const messages = [];
        Object.keys(serverErrors).forEach((field) => {
          serverErrors[field].forEach((msg) => messages.push(msg));
        });
        setValidationErrors(messages);
      } else {
        setError(err?.response?.data?.message || 'Failed to create request. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData(initialExitEntryForm);
    setError('');
    setValidationErrors([]);
  };

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
      <PageHeader
        compact
        variant="dashboard"
        overline="Requests"
        title="Exit/Entry Request"
        subtitle="Submit travel validity, visa type, destination contact details, and optional supporting documents."
        actions={[
          <Button key="back" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/requests/new')}>
            Request Catalog
          </Button>,
        ]}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        <SectionCard
          tone="dashboard"
          title="Travel Details"
          subheader="Required fields are dates and visa type. Optional details help HR review faster."
          contentSx={{ p: { xs: 1.35, md: 1.7 } }}
        >
          <Stack spacing={1.5}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            {validationErrors.length ? (
              <Alert severity="error">
                <List dense disablePadding>
                  {validationErrors.map((msg) => (
                    <ListItem key={msg} sx={{ py: 0 }}>
                      {msg}
                    </ListItem>
                  ))}
                </List>
              </Alert>
            ) : null}

            <Paper
              variant="outlined"
              sx={{
                p: 1.15,
                borderRadius: theme.hr?.radius?.md || 2,
                borderColor: alpha(theme.palette.primary.main, 0.16),
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.035),
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ChecklistRoundedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Submission readiness
                  </Typography>
                </Stack>
                <Chip
                  size="small"
                  color={completionPct === 100 ? 'success' : 'warning'}
                  variant="outlined"
                  label={`${completionPct}% complete`}
                  sx={{ fontWeight: 850 }}
                />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={completionPct}
                sx={{
                  mt: 1,
                  height: 7,
                  borderRadius: 999,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    background: theme.hr?.gradient?.success,
                  },
                }}
              />
            </Paper>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={1.6}>
                <FieldGroup
                  icon={<CalendarMonthRoundedIcon color="primary" fontSize="small" />}
                  title="Travel Window"
                  description="Set the validity range and visa category."
                >
                  <FieldGrid>
                    <TextField
                      label="Validity From Date"
                      type="date"
                      name="validity_from_date"
                      value={formData.validity_from_date}
                      onChange={handleChange}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                    <TextField
                      label="Validity To Date"
                      type="date"
                      name="validity_to_date"
                      value={formData.validity_to_date}
                      onChange={handleChange}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                    <FormControl fullWidth required sx={fieldSx}>
                      <InputLabel>Visa Type</InputLabel>
                      <Select name="visa_type" label="Visa Type" value={formData.visa_type} onChange={handleChange}>
                        {visaOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option === 'choose' ? 'Choose visa type' : option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Period in Days (optional)"
                      type="number"
                      name="period_in_days"
                      value={formData.period_in_days}
                      onChange={handleChange}
                      fullWidth
                      inputProps={{ min: 0 }}
                      helperText={calculatedDays ? `Date range suggests ${calculatedDays} day(s).` : 'Optional manual value.'}
                      sx={fieldSx}
                    />
                  </FieldGrid>
                </FieldGroup>

                <Divider />

                <FieldGroup
                  icon={<PublicRoundedIcon color="primary" fontSize="small" />}
                  title="Destination & Contact"
                  description="Optional destination information for HR reference."
                >
                  <FieldGrid>
                    <TextField
                      label="Country (optional)"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      fullWidth
                      sx={fieldSx}
                    />
                    <TextField
                      label="Phone Number (optional)"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleChange}
                      fullWidth
                      sx={fieldSx}
                    />
                    <Box sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}>
                      <TextField
                        label="Location in Country (optional)"
                        name="location_in_country"
                        value={formData.location_in_country}
                        onChange={handleChange}
                        fullWidth
                        sx={fieldSx}
                      />
                    </Box>
                  </FieldGrid>
                </FieldGroup>

                <Divider />

                <FieldGroup
                  icon={<DescriptionRoundedIcon color="primary" fontSize="small" />}
                  title="Supporting Information"
                  description="Add context and attach a document if available."
                >
                  <Stack spacing={1.2}>
                    <TextField
                      label="Reason (optional)"
                      name="reason"
                      value={formData.reason}
                      onChange={handleChange}
                      multiline
                      rows={4}
                      fullWidth
                      placeholder="Provide purpose and context for the request."
                      sx={fieldSx}
                    />

                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: theme.hr?.radius?.md || 2,
                        p: 1,
                        borderStyle: 'dashed',
                        borderColor: alpha(theme.palette.primary.main, 0.28),
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.025),
                      }}
                    >
                      <Button
                        variant="text"
                        component="label"
                        fullWidth
                        startIcon={<AttachFileRoundedIcon />}
                        sx={{ justifyContent: 'flex-start', px: 1 }}
                      >
                        Upload Supporting Document (optional)
                        <input
                          type="file"
                          name="document"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          hidden
                          onChange={handleChange}
                        />
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block' }}>
                        Allowed: PDF, DOC, DOCX, JPG, JPEG, PNG
                      </Typography>
                      {formData.document ? (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ mt: 1, ml: 1, maxWidth: 'calc(100% - 16px)' }}
                          label={`Selected: ${formData.document.name}`}
                        />
                      ) : null}
                    </Paper>
                  </Stack>
                </FieldGroup>

                <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} justifyContent="flex-end">
                  <Button type="button" variant="outlined" color="inherit" startIcon={<RestartAltRoundedIcon />} onClick={handleReset}>
                    Reset
                  </Button>
                  <Button type="submit" variant="contained" startIcon={loading ? null : <SendRoundedIcon />} disabled={loading}>
                    {loading ? <CircularProgress size={22} color="inherit" /> : 'Submit Request'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </SectionCard>

        <Stack spacing={1.5} sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
          <SectionCard
            tone="dashboard"
            title="Request Summary"
            subheader="Updates from the form while you type."
            contentSx={{ p: { xs: 1.35, md: 1.6 } }}
          >
            <Stack spacing={1.2}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.15,
                  borderRadius: theme.hr?.radius?.md || 2,
                  borderColor: alpha(theme.palette.secondary.main, 0.2),
                  bgcolor: alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.12 : 0.055),
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <FlightTakeoffIcon color="secondary" fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Visa Summary
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  <Chip size="small" variant="outlined" label={selectedVisaType} />
                  <Chip size="small" variant="outlined" label={`${calculatedDays || period_in_days || 0} day(s)`} />
                </Stack>
              </Paper>

              <SummaryRow icon={<TimelineRoundedIcon fontSize="small" />} label="Validity From" value={validity_from_date} />
              <SummaryRow icon={<CalendarMonthRoundedIcon fontSize="small" />} label="Validity To" value={validity_to_date} />
              <SummaryRow icon={<PublicRoundedIcon fontSize="small" />} label="Country" value={formData.country || 'Country not set'} />
              <SummaryRow icon={<PhoneIphoneRoundedIcon fontSize="small" />} label="Phone" value={formData.phone_number || 'Phone not set'} />
              <SummaryRow icon={<PlaceRoundedIcon fontSize="small" />} label="Location" value={formData.location_in_country || 'Location not set'} />
            </Stack>
          </SectionCard>

          <SectionCard
            tone="dashboard"
            title="Approval Path"
            subheader="Expected review sequence after submission."
            contentSx={{ p: { xs: 1.25, md: 1.45 } }}
          >
            <Stack spacing={1}>
              {exitEntryWorkflowSteps.map((step, index) => (
                <Box
                  key={`${step.actor}-${index}`}
                  sx={{
                    p: 1,
                    borderRadius: theme.hr?.radius?.md || 2,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.14),
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.025),
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Chip size="small" label={`Step ${index + 1}`} variant="outlined" color="primary" sx={{ minWidth: 66, fontWeight: 800 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        {step.actor}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {step.action}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </SectionCard>
        </Stack>
      </Box>
    </PageLayout>
  );
}
